import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  useTheme,
  CircularProgress,
} from '@mui/material';
import { useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { getDisplayCode } from '../utils';
import { pickDesiredConsultationCode } from 'FRONTEND/reducers/bookingSlice';
import { useGetConsultationCodesForUserQuery } from 'FRONTEND/utils/backendApi/consultationCodesApi';
import i18n from 'FRONTEND/utils/i18n';


const ConsultationCodeSelectField = ({
  calendarActorId,
  onCodeChange,
  ...otherProps
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const textStyling = {
    fontSize: '1.1rem',
    color: theme.palette.primary.main,
  };

  const locale = useSelector((state) => state.user.locale);
  const selectedExpertId = useSelector((state) => state.booking.selectedExpertId);
  const desiredConsultationCode = useSelector((state) => state.booking.desiredConsultationCode);

  const { data: consultationCodes, isLoading, isError } = useGetConsultationCodesForUserQuery(selectedExpertId || calendarActorId, { skip: (!selectedExpertId && !calendarActorId) });
  useMemo(() => {
    const code = consultationCodes?.find(code => code.isDefault);
    dispatch(pickDesiredConsultationCode({ code: code || null }));
  }, [consultationCodes]);


  const handleSelectCode = (code) => {
    dispatch(pickDesiredConsultationCode({ code }));
    onCodeChange(code || null);
  };

  if (isLoading) {
    return <CircularProgress />;
  }

  // Don't render anything if loading or has error or no codes
  if (isError || !consultationCodes || consultationCodes.length === 0) {
    return (
      <Typography color='error' variant='body2'>
        {i18n._('An error occurred while processing your request.')}
      </Typography>
    );
  }

  // Single consultation code - display as disabled TextField
  if (consultationCodes.length === 1) {
    return (
      <FormControl fullWidth variant='standard' {...otherProps}>
        <Typography sx={{ fontSize: '12px', color: 'primary.main', mb: 0.5 }}>
          {i18n._('Consultation fee predefined by the practitioner')}
        </Typography>
        <TextField
          variant='standard'
          value={getDisplayCode(consultationCodes[0], locale)}
          disabled
          slotProps={{
            input: {
              disableUnderline: false,
            },
          }}
          sx={{
            ...textStyling,
          }}
        />
      </FormControl>
    );
  }

  // Multiple consultation codes - display as Select dropdown
  return (
    <FormControl fullWidth variant='standard' sx={{ mt: 2 }} {...otherProps}>
      <InputLabel id='rate-select-label'>
        {i18n._('Consultation fee predefined by the practitioner')}
      </InputLabel>
      <Select
        labelId='rate-select-label'
        value={desiredConsultationCode?.id || ''}
        onChange={(e) => {
          const selected = consultationCodes.find(code => code.id === e.target.value);
          handleSelectCode(selected);
        }}
        sx={{
          '& .MuiSelect-select': {
            paddingLeft: '8px',
          },
          ...textStyling,
        }}
      >
        {consultationCodes.map((code, index) => (
          <MenuItem key={index} value={code.id}>
            {getDisplayCode(code, locale)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default ConsultationCodeSelectField;
